import { TEXT_LINK_FOCUS_RING } from "../lib/a11yFocus";
import { useEffect, useState } from "react";
import { generatePath, Link } from "react-router-dom";
import { Button, InlineAlert } from "../components/BaseComponents";
import PageHeadings from "../components/BaseComponents/PageHeadings/PageHeadings";
import PageTemplate from "../components/BaseComponents/PageTemplate/PageTemplate";
import SimpleSortTable, { TableColumn } from "../components/BaseComponents/SimpleSortTable/SimpleSortTable";
import routes from "../components/Router/routes";
import useApi from "../hooks/useApi";
import useAccessScope from "../hooks/useAccessScope";
import { formatControlRequestStatus, formatDate, getApiErrorMessage } from "../lib/utils";
import { formatDelegatedAccessLevel } from "../lib/delegatedAccess";

function ClientCompanies() {

    const [loading, setLoading] = useState(true);
    const api = useApi();
    const { generation, isCurrent } = useAccessScope(api);
    const [loadedGeneration, setLoadedGeneration] = useState<number>();
    const [data, setData] = useState<any[]>([])
    // Issue #884: this GET only ever runs for an admin-kind assumption into a
    // clientAccounting-enabled organisation (buildAssumedAccess forces clientAccounting false for
    // delegated-kind assumptions), but it is reachable that way, and can now reject with
    // ACCESS_AUDIT_UNAVAILABLE (#857 item 6) instead of hanging forever. The global notify()
    // (UserContext) already tells the user why; this just stops the table spinning forever and
    // shows a visible, existing-shape message instead of silently staying on "no client
    // organisations" (which would misreport an empty list as the real state).
    const [loadError, setLoadError] = useState('');

    useEffect(() => {
        const controller = new AbortController();
        setData([]);
        setLoadedGeneration(generation);
        setLoading(true);
        setLoadError('');
        const run = async () =>{ 
            try {
                const result = await api.get('/delegatedAccess/mine?limit=100', { signal: controller.signal })
                if (!controller.signal.aborted && isCurrent(generation)) {
                    setData(result.data.map((grant: any) => ({ ...grant, _id: grant.id })))
                }
            } catch (err) {
                if (!controller.signal.aborted && isCurrent(generation)) {
                    setLoadError(getApiErrorMessage(err, 'Failed to load client organisations. Please try again.'))
                }
            } finally {
                if (!controller.signal.aborted && isCurrent(generation)) setLoading(false);
            }
        }

        run()
        return () => controller.abort()
    }, [api, generation, isCurrent])

    const columns: TableColumn[] = [
        {
            id: 'grantorCompanyName',
            label: 'Organisation name',
            renderer: (key, data) => {
                const controlPath = generatePath(
                    routes.assumeOrganisation.path,
                    {assumptionType: 'delegated', companyId: data.grantorCompanyId}
                );
                
                const hasAccess = data.status === 'active'
                return hasAccess ? (
                    <Link
                        className={`text-link ${TEXT_LINK_FOCUS_RING}`}
                        to={controlPath}
                    >
                        { data.grantorCompanyName }
                    </Link>
                ) : (
                    <span>{ data.grantorCompanyName }</span>
                );
            }
        },
        {
            id: 'grantorCompanyNumber',
            label: 'Registered #'
        },
        {
            id: 'status',
            label: 'Status',
            renderer: (key, data) => <span>{ formatControlRequestStatus(data[key]) }</span>
        },
        {
            id: 'accessLevel',
            label: 'Access level',
            renderer: (key, data) => <span>{ data.status === 'active' ? formatDelegatedAccessLevel(data[key]) : '' }</span>
        },
        {
            id: 'grantorJurisdiction',
            label: 'Jurisdiction',
        },
        {
            id: 'grantorAddress',
            label: 'Address',
        },
        {
            id: 'grantorLatestReport',
            label: 'Latest Report',
            renderer: (key, data) =>{ 
                const value = data[key];
                return <span>{ value ? formatDate(data[key]) : null }</span>
            }
        },
    ]

    return (
        <PageTemplate>
            <PageHeadings title="Client Organisations"/>
            <div className="mt-8">
                <Button
                    requiresWrite
                    type="Primary"
                    text="Invite organisation"
                    hasLeadingIcon
                    icon="fa-solid fa-user-plus"
                    size="sm"
                    isLink
                    url={routes.publicProfiles.path}
                />
            </div>
            { loadedGeneration === generation && loadError && <InlineAlert type="error" className="mt-8">{loadError}</InlineAlert> }
            <SimpleSortTable
                additionalClass="mt-8"
                data={loadedGeneration === generation ? data : []}
                columns={columns}
                isLoading={loading || loadedGeneration !== generation}
            />
        </PageTemplate>
    )
}

export default ClientCompanies;